import {
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import withStyles from "@mui/styles/withStyles";
import { CheckBox, Close, LockOutlined, PublicOutlined } from "@mui/icons-material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import MButton from "components/momentum/MButton";
import "moment-timezone";
import {
  useAccountById,
  useCompetitorsByKey,
  useMetadataByCategory,
  useNoteById,
} from "components/summary/hooks";
import { useForm, Controller, useWatch } from "react-hook-form";
import { toast } from "react-toastify";
import { useAxios } from "myAxios";
import { Note } from "types/summary";
import FileUploader, { FileUploaderRef } from "components/app/FileUploader";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Matadata } from "types/app";
import { AxiosResponse } from "axios";
import useTinyMCE from "components/app/useTinyMCE";

const useStyles = makeStyles(() => ({
  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    padding: "16px 24px",
    alignItems: "center",
  },
  title: {
    fontSize: 16,
    color: "#171B1F",
    fontWeight: 500,
    display: "flex",
    lineHeight: "24px",
  },
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  fieldHalf: {
    marginTop: 8,
    flexBasis: "48%",
    "&:nth-of-type(2n)": {
      marginLeft: "4%",
    },
  },
  fieldTitle: {
    color: "#121212",
    fontSize: 16,
    marginRight: 4,
    alignItems: "center",
    display: "flex",
    marginBottom: 8,
  },
  tooltipIcon: {
    color: "#00a0d1",
    fontSize: "1rem",
    marginLeft: 4,
  },
  tooltip: {
    marginLeft: 4,
  },
  roleContainer: {
    border: "1px solid #B4B6B8",
    borderRadius: 4,
    marginTop: 16,
  },
  roleTitle: {
    backgroundColor: "#F7F7F7",
    padding: 16,
    fontSize: 14,
    fontWeight: 500,
    textAlign: "center",
    borderRadius: 4,
  },
  otherInput: {
    paddingTop: 8,
    paddingBottom: 8,
  },
  dropzone: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
    borderWidth: 2,
    borderRadius: 2,
    borderColor: "#eeeeee",
    borderStyle: "dashed",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
  fieldText: {
    color: "#545454",
    fontSize: 14,
    textAlign: "right",
  },
  action: {
    padding: 16,
  },
  field: {
    display: "flex",
    alignItems: "center",
    marginTop: 24,
  },
  fieldLabel: {
    fontSize: 12,
    color: "#535759",
    fontWeight: 500,
  },
  checkbox: {
    display: "none",
    position: "absolute",
    top: -2,
    right: -2,
    fontSize: "0.85rem",
    borderRadius: 4,
    ".Mui-selected &": {
      display: "block",
    },
  },
}));

export interface NoteDialogProps {
  accountId?: string;
  type: "Add New" | "Update" | "Competitor";
  open: boolean;
  onClose: () => void;
  data?: Note;
  noteOptions?: Matadata[];
  placeholder?: string;
  readonly?: boolean;
  page?: string;
  from?: string;
}

const StyledToggleButton = withStyles({
  selected: {
    color: "#00a0d1 !important",
    backgroundColor: "rgba(230, 251, 255, 0.5) !important",
    fontWeight: 700,
    borderColor: "#00a0d1 !important",
  },
  root: {
    padding: "8px 0px",
    color: "#121212",
    border: "1px solid #DEDEDE",
    borderRadius: "4px !important",
    "&:not(.Mui-selected)": {
      borderColor: "#DEDEDE !important",
    },
  },
})(ToggleButton);

const NoteDialog: React.FC<NoteDialogProps> = ({
  accountId,
  data,
  type,
  open,
  onClose,
  noteOptions,
  readonly,
  page = "Detail",
  placeholder = "Public notes are visible to anyone with access to this account including Cisco Collab CSMs and PSMs as well as authorized partners. Private notes are only visible to users within same organization",
  from,
}) => {
  const axios = useAxios();
  const classes = useStyles();

  const fileUploaderRef = useRef<FileUploaderRef>(null);

  const { mutate } = useNoteById(accountId);
  const { data: account } = useAccountById(accountId);
  const { mutate: mutateCompetitor } = useCompetitorsByKey(account?.accountKey);

  const [updating, setUpdating] = useState(false);

  const { handleSubmit, control, reset, setValue } = useForm();

  // const [fullScreen, setFullScreen] = useState(false);

  const DEFAULT_HEIGHT = "calc(100vh - 590px)";

  // const FULL_SCREEN_HEIGHT = "calc(100vh - 330px)";
  //
  // const READONLY_FULL_SCREEN_HEIGHT = "calc(100vh - 200px)";

  const { editor, editorRef, isFullScreen } = useTinyMCE({
    init: {
      height: DEFAULT_HEIGHT,
      placeholder,
      readonly,
    },
    initialValue: data?.notes ?? "",
  });

  useEffect(() => {
    if (open) {
      if (data) {
        setValue(
          "notesVisibility",
          data ? (data.confidential === 2 ? "PRIVATE" : "PUBLIC") : "PRIVATE"
        );
        setValue("noteType", data?.noteType ?? "");
      }
    } else {
      if (type === "Update") {
        reset();
      }
      // reset();
    }
  }, [open, data, type]);

  const selectedType = useWatch({
    control,
    name: "noteType",
    defaultValue: "",
  });

  const { data: lovs } = useMetadataByCategory("NOTE_TYPE");

  const options = useMemo(
    () => noteOptions ?? lovs?.filter((x) => x.description !== null),
    [lovs, noteOptions]
  );

  const noteSizeError = (e?: { response: AxiosResponse }) => {
    if (e?.response?.status === 413) {
      toast.error("Your upload exceeds the max limit of 31 mega bytes.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const onCreate = async (submitData: { [key: string]: string }) => {
    const postData = {
      accountId,
      ...submitData,
    };

    if (type === "Competitor") {
      const data = {
        comment: submitData.notes,
        competitorName: submitData?.noteType?.split("_")?.[1] ?? "",
        siteName: submitData.siteName,
      };
      let noteId = "";
      try {
        await axios.post(`/api/dataservice/v1/accounts/${account?.accountKey}/competitors`, data);
        mutateCompetitor();
      } catch (e) {
        setUpdating(false);
        toast.error(e?.response?.data?.message || "Fail to add competitor", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }
      try {
        await mutateCompetitor();
        const { data } = await axios.post<string>(`/api/dataservice/v1/notes`, postData);
        noteId = data;
      } catch (e) {
        setUpdating(false);
        toast.error(e?.response?.data?.message || "Fail to add note", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }

      try {
        await fileUploaderRef.current?.submit({ noteId });
        toast.success("Notes added successfully", {
          position: "top-center",
          autoClose: 2000,
        });
      } catch (e) {
        setUpdating(false);
        toast.error("Notes added, but fail to upload files", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } else {
      let noteId = "";
      try {
        const { data } = await axios.post<string>(`/api/dataservice/v1/notes`, postData);
        noteId = data;
        mutate();
      } catch (e) {
        setUpdating(false);
        noteSizeError(e);
        toast.error(e?.response?.data?.message || "Fail to add note", {
          position: "top-center",
          autoClose: 3000,
        });
        return;
      }
      try {
        await fileUploaderRef.current?.submit({ noteId });
        toast.success("Notes added successfully", {
          position: "top-center",
          autoClose: 2000,
        });
        reset();
      } catch (e) {
        noteSizeError(e);
        setUpdating(false);
        toast.error("Notes added, but fail to upload files", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  };

  const onUpdate = async (submitData: { [key: string]: string }) => {
    const postData = {
      ...submitData,
    };

    try {
      await axios.put(`/api/dataservice/v1/notes/${data?.id}`, postData);
      mutate();
    } catch (e) {
      setUpdating(false);
      noteSizeError(e);
      toast.error(e?.response?.data?.message || "Fail to updated note", {
        position: "top-center",
        autoClose: 3000,
      });
    }

    try {
      await fileUploaderRef.current?.submit({ noteId: data?.id || "" });
    } catch (e) {
      setUpdating(false);
      noteSizeError(e);
      toast.error("Notes updated, but fail to upload files", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    const currentFiles = fileUploaderRef.current?.getUploadedFiles();
    const deletedFiles =
      data?.files?.filter((x) => !currentFiles?.find((y) => y.fileId === x.fileId)) || [];
    if (deletedFiles.length) {
      await axios.delete(`/api/dataservice/v1/notes/${data?.id}/files`, {
        data: deletedFiles.map((x) => x.fileId),
      });
    }
    mutate?.();

    toast.success("Notes updated successfully", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  const onSubmit = async (submitData: { [key: string]: string }) => {
    const notes = editorRef.current?.getContent() ?? "";
    if (!notes) {
      toast.error("Please input some notes", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    if (isFullScreen) editorRef.current?.execCommand("mceFullScreen");
    setUpdating(true);
    const data = {
      ...submitData,
      notes,
    };
    if (type === "Add New" || type === "Competitor") {
      await onCreate(data);
    } else {
      await onUpdate(data);
    }
    setUpdating(false);
    onClose();
  };

  const onError = (errors: { [key: string]: any }) => {
    if (Object.keys(errors).length) {
      toast.error("Please correct the highlighted field(s)", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  const fileUploaderMemo = useMemo(
    () => (
      <FileUploader
        ref={fileUploaderRef}
        note={data}
        displayUploadedOnly={readonly}
        submitURL={"/dataservice/v1/notes/{noteId}/files"}
        // deleteURL={"/dataservice/v1/notes/{id}/files"}
        dryrun={true}
      />
    ),
    [data]
  );

  return (
    <Dialog
      open={open}
      // scroll={"body"}
      fullWidth={true}
      maxWidth={"md"}
      // onClose={onClose}
      disableEscapeKeyDown
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
      }}
      hideBackdrop={true}
      disableEnforceFocus={true}
    >
      <form
        onSubmit={handleSubmit(onSubmit, onError)}
        style={{ height: "100%" }}
      >
        <div className={classes.titleContainer}>
          <div>
            <>
              <p className={classes.title}>Note for {account?.accountName}</p>
              <p style={{ color: "#666666", fontSize: 12 }}>{account?.accountId}</p>
            </>
          </div>
          <IconButton
            onClick={onClose}
            size="small"
          >
            <Close style={{ color: "#121212", fillOpacity: 0.6 }} />
          </IconButton>
        </div>
        <DialogContent
          dividers={true}
          style={{
            paddingTop: 0,
            filter: updating ? "opacity(0.5)" : undefined,
            pointerEvents: updating ? "none" : "auto",
          }}
        >
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Visibility: </span>
            <Controller
              name="notesVisibility"
              control={control}
              defaultValue={data ? (data.confidential === 2 ? "PRIVATE" : "PUBLIC") : "PRIVATE"}
              render={({ field }) => (
                <ToggleButtonGroup
                  {...field}
                  onChange={(_e, v) => (readonly ? undefined : v && field.onChange(v))}
                  exclusive={true}
                  style={{ marginLeft: 26 }}
                >
                  <StyledToggleButton
                    value="PRIVATE"
                    style={{ width: 134, justifyContent: "center" }}
                  >
                    <Tooltip title="Private notes are only visible to users within same organization.">
                      <span
                        style={{
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <LockOutlined style={{ marginRight: 12, fontSize: "1.25rem" }} />
                        Private
                        <CheckBox className={classes.checkbox} />
                      </span>
                    </Tooltip>
                  </StyledToggleButton>
                  <StyledToggleButton
                    value="PUBLIC"
                    style={{
                      marginLeft: 12,
                      width: 134,
                      justifyContent: "center",
                    }}
                  >
                    <Tooltip title="Public notes are visible to anyone with access to this account including Cisco Collab CSMs and PSMs as well as authorized partners.">
                      <span
                        style={{
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <PublicOutlined style={{ marginRight: 12, fontSize: "1.25rem" }} />
                        Public
                        <CheckBox className={classes.checkbox} />
                      </span>
                    </Tooltip>
                  </StyledToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </div>
          <div className={classes.field}>
            <span className={classes.fieldLabel}>Note Type: </span>
            <Controller
              name="noteType"
              control={control}
              defaultValue={data?.noteType ?? ""}
              rules={{ required: { message: "Please select", value: true } }}
              render={({ fieldState, field }) => (
                <TextField
                  {...field}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  select={true}
                  SelectProps={{
                    displayEmpty: true,
                    readOnly: readonly,
                  }}
                  variant="outlined"
                  size="small"
                  style={{ marginLeft: 16, width: 280 }}
                >
                  <MenuItem value="">
                    {type === "Competitor" ? "Select Competitor Option" : "Select Note Type"}
                  </MenuItem>
                  {options?.map((x) => (
                    <MenuItem
                      key={x.id}
                      value={x.code}
                    >
                      {x.value}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>
          <p
            className={classes.fieldLabel}
            style={{ marginTop: 24 }}
          >
            Reason For Contact:
          </p>
          <div style={{ margin: "8px 0" }}>{editor}</div>
          {type === "Competitor" && selectedType ? (
            <div style={{ display: "flex", alignItems: "center" }}>
              <Controller
                name="siteName"
                control={control}
                defaultValue={data?.noteType ?? ""}
                rules={{
                  required: { message: "Please input", value: true },
                  pattern: {
                    value: /^[A-Za-z0-9\-\.]+$/,
                    message: "sitename invalid",
                  },
                }}
                render={({ fieldState, field }) => (
                  <TextField
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    variant="outlined"
                    size="small"
                    placeholder="Add Competitor URL"
                  />
                )}
              />
              <p>{noteOptions?.find((x) => x.code === selectedType)?.description ?? ""}</p>
            </div>
          ) : null}
          <div style={{ marginTop: 16 }}>{fileUploaderMemo}</div>
        </DialogContent>
        {readonly ? null : (
          <DialogActions className={classes.action}>
            <MButton
              style={
                isFullScreen
                  ? { position: "fixed", bottom: 16, right: 16, zIndex: 1300 }
                  : undefined
              }
              type="submit"
              buttonType="blue"
              buttonSize={36}
              onClick={handleSubmit(onSubmit, onError)}
              trackName={
                type === "Competitor"
                  ? "Add New URLs"
                  : type === "Add New"
                    ? from === "Quick Link"
                      ? `Quick Link Add a Note Submit`
                      : `[${page}] Add a Note`
                    : `[${page}] Update Note`
              }
            >
              Submit
            </MButton>
          </DialogActions>
        )}
      </form>
    </Dialog>
  );
};

export default NoteDialog;
